import { DefaultLink } from '~/components/Links/DefaultLink';
import { CorporateNavigationBlockProps } from '@oresundsbron/api/src/types/contentful/CorporateBannerNavigationBlock';
import Image from 'next/image';
import { ArrowRight2 } from '@oresundsbron/icons';
import { cx } from 'class-variance-authority';
import { useLinks } from '~/hooks/useLinks';

interface CorporateNavigationBlockPropsType {
  content: CorporateNavigationBlockProps;
}

const CorporateNavigationBlock = ({
  content: { appearance, links, title, className },
}: CorporateNavigationBlockPropsType) => {
  const { getLink } = useLinks();

  return (
    <>
      {appearance === 'Level 1' && (
        <nav className={cx('sm:mx-0', className)}>
          <ul className="mx-auto grid max-w-7xl auto-cols-fr overflow-hidden rounded-3xl bg-blue-50 shadow-md sm:grid-flow-col">
            {links.map((entry, index) => {
              const link = entry.linkExternal
                ? entry.linkExternal
                : entry.link?.sys.id
                ? getLink(entry.link.sys.id)?.path
                : undefined;
              return (
                <li
                  key={entry.sys.id + index}
                  className="border-t first:border-0 sm:border-l sm:border-t-0"
                >
                  <DefaultLink
                    href={link || ''}
                    className={cx(
                      ' block h-full border-blue-200 p-6 text-blue-400 no-underline transition-all',
                      link && 'group hover:bg-blue-100 hover:no-underline'
                    )}
                  >
                    <div className="flex w-full items-center justify-between">
                      <div>
                        {entry.media?.url && (
                          <Image
                            src={entry.media.url}
                            alt={entry.media.title}
                            height={48}
                            width={48}
                            className="mb-8"
                            unoptimized // Will not render nice SVGs without this
                          />
                        )}
                      </div>
                      <div>
                        <ArrowRight2 className="mb-8 ml-auto block h-12 w-12 text-neutral-800 sm:hidden" />
                      </div>
                    </div>
                    <div
                      className="break-words  text-2xl font-semibold text-type-heading group-hover:underline"
                      role="heading"
                      aria-level={2}
                    >
                      {entry.title}
                    </div>
                    {entry.preamble && (
                      <div className="mt-4 text-type-secondary">
                        {entry.preamble}
                      </div>
                    )}
                  </DefaultLink>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
      {appearance === 'Level 2' && (
        <nav className={cx('mt-6 sm:mx-0 sm:-mt-40', className)}>
          {links.length > 0 && (
            <ul className="mx-auto grid max-w-7xl grid-cols-1 gap-px overflow-hidden rounded-3xl bg-blue-200 sm:grid-cols-3">
              {links.map((entry, index) => {
                const link = entry.linkExternal
                  ? entry.linkExternal
                  : entry.link?.sys.id
                  ? getLink(entry.link.sys.id)?.path
                  : undefined;
                return (
                  <li key={entry.sys.id + index} className="bg-blue-50">
                    <DefaultLink
                      href={link || ''}
                      className={cx(
                        'flex h-full flex-col p-8 text-blue-400 no-underline transition-all',
                        link && 'group hover:bg-blue-100 hover:no-underline'
                      )}
                    >
                      <div className="flex w-full items-center justify-between break-words text-2xl font-semibold text-type-heading group-hover:underline">
                        <div role="heading" aria-level={2}>
                          {entry.title}
                        </div>
                        {link && (
                          <div>
                            <ArrowRight2 className="ml-auto block h-12 w-12 text-neutral-800 sm:hidden" />
                          </div>
                        )}
                      </div>
                      {entry.preamble && (
                        <div className="mt-4 text-type-secondary">
                          {entry.preamble}
                        </div>
                      )}
                      {link && (
                        <div className="mt-auto">
                          <ArrowRight2 className="mt-8 hidden h-12 w-12 text-neutral-800 sm:block" />
                        </div>
                      )}
                    </DefaultLink>
                  </li>
                );
              })}
              {/* Always fill the row. Eg, if five items in array, render one empty. If seven entries in array, render two empty.
                  Reason is it looks wierd because of the background color of the <ul> that makes up the divider grid */}
              {Array.from({ length: (3 - (links.length % 3)) % 3 }).map(
                (_, index) => (
                  <li
                    key={`empty-${index}`}
                    className="hidden bg-blue-50 p-8 sm:block"
                  ></li>
                )
              )}
            </ul>
          )}
        </nav>
      )}
      {appearance === 'Up next' && (
        <div
          className={`mx-auto grid max-w-7xl grid-cols-1 gap-4 sm:gap-10  ${
            className && className
          }`}
        >
          {title && (
            <div className="text-center text-2xl font-semibold text-type-heading ">
              {title}
            </div>
          )}
          {links.length > 0 && (
            <ul className="flex flex-col gap-4 sm:flex-row sm:items-stretch sm:gap-8">
              {links.map((entry, index) => {
                const link = entry.linkExternal
                  ? entry.linkExternal
                  : entry.link?.sys.id
                  ? getLink(entry.link.sys.id)?.path
                  : undefined;
                return (
                  <li
                    className="flex-1 rounded-2xl border-neutral-50 bg-neutral-50 pb-6 shadow-md"
                    key={entry.sys.id + index}
                  >
                    <div className="">
                      <DefaultLink
                        href={link || ''}
                        className={cx(
                          'gap-6 text-blue-400 no-underline',
                          link && 'group hover:no-underline'
                        )}
                      >
                        <span>
                          {entry.media?.url && (
                            <Image
                              className="aspect-video w-full rounded-t-2xl object-cover"
                              src={entry.media.url}
                              alt={entry.media.title}
                              height={650}
                              width={650}
                              sizes="(min-width: 1024px) 650px, (min-width: 640px) 50vw, 100vw"
                            />
                          )}
                        </span>
                        <span className="inline-block p-4">
                          <div
                            className="blue-950 break-words text-2xl font-semibold leading-7 text-type-heading group-hover:underline"
                            role="heading"
                            aria-level={2}
                          >
                            {entry.title}
                          </div>
                          {entry.preamble && (
                            <p className="mt-4 text-base font-normal text-neutral-600 ">
                              {entry.preamble}
                            </p>
                          )}
                        </span>
                      </DefaultLink>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {appearance !== 'Level 1' &&
        appearance !== 'Level 2' &&
        appearance !== 'Up next' && <div>No appearance selected.</div>}
    </>
  );
};

export default CorporateNavigationBlock;
